import { cn } from 'src/utilities/cn';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  'inline-flex relative h7 no-underline cursor-pointer items-center justify-center whitespace-nowrap ring-offset-background ring-transparent transition focus-visible:outline-hidden ring-4 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-12 h7 px-6 py-2 md:h-10 rounded-full md:px-4 md:py-1',
        sm: 'h-10 px-4 rounded-full md:h-8',
        icon: 'h-12 w-12 rounded-full',
        clear: '',
      },
      variant: {
        default: 'bg-blue text-white hover:ring-blue/50',
        'outline-blue': 'border border-blue text-blue hover:ring-blue/50',
        white: 'bg-white text-blue hover:ring-white/50',
        link: 'text-primary! items-start justify-start underline-offset-4 hover:underline',
      },
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  loading?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  loading,
  children,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';

  if (loading && !asChild) {
    return (
      <button
        className={cn(buttonVariants({ className, size, variant }))}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }

  return (
    <Comp
      className={cn(buttonVariants({ className, size, variant }))}
      ref={ref}
      {...props}
    >
      {children}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
